<script setup lang="ts">
import { formatCnpjTyping } from '@solfacil/frontend-utils/cnpj';
import { formatCpfTyping } from '@solfacil/frontend-utils/cpf';
import { IconAlertTriangle } from '@tabler/icons-vue';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, watch } from 'vue';

import GenericModal from '~/components/shared/GenericModal.vue';
import { formatDocumentForDisplay } from '~/utils/formatters';

import useTitularityTransfer from '../queries/formalization/useTitularityTransfer';
import { createTitularityTransferSchema, type TitularityTransferForm } from '../schemas/titularityTransferSchema';

interface Props {
  currentDocument: string;
  currentName: string;
  financingProjectId: string;
  modelValue: boolean;
  projectId: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const { isPending: isLoading, mutate: transferTitularity } = useTitularityTransfer();

const {
  errors,
  handleSubmit,
  meta,
  resetForm,
  setFieldValue,
  values,
} = useForm<TitularityTransferForm>({
  initialValues: { newDocument: '' },
  validationSchema: computed(() => toTypedSchema(createTitularityTransferSchema(props.currentDocument))),
});

const maskDocument = (raw: string) => {
  const digits = raw.replace(/\D/g, '').slice(0, 14);
  return digits.length > 11 ? formatCnpjTyping(digits) : formatCpfTyping(digits);
};

const handleInput = (event: Event) => {
  setFieldValue('newDocument', maskDocument((event.target as HTMLInputElement).value));
};

const handleSave = handleSubmit((formValues) => {
  transferTitularity(
    {
      financingProjectId: props.financingProjectId,
      newCustomerDocument: formValues.newDocument.replace(/\D/g, ''),
      projectId: props.projectId,
    },
    { onSuccess: () => { isVisible.value = false; } },
  );
});

const handleCancel = () => {
  if (!isLoading.value) isVisible.value = false;
};

watch(isVisible, (open) => {
  if (!open) resetForm();
});
</script>

<template>
  <GenericModal
    v-model="isVisible"
    title="Trocar titularidade"
    primary-button-text="Confirmar troca"
    :loading="isLoading"
    :primary-button-disabled="!meta.valid"
    @primary-action="handleSave"
    @secondary-action="handleCancel"
  >
    <div class="mb-3">
      <div class="text-secondary small">Titular atual</div>
      <div class="fw-bold">{{ currentName }}</div>
      <div>{{ formatDocumentForDisplay(currentDocument) }}</div>
    </div>

    <div class="mb-3">
      <label for="new-titularity-document" class="form-label required">Novo CPF/CNPJ</label>
      <input
        id="new-titularity-document"
        class="form-control"
        :class="{ 'is-invalid': values.newDocument && errors.newDocument }"
        inputmode="numeric"
        placeholder="Digite o CPF ou CNPJ do novo titular"
        type="text"
        :disabled="isLoading"
        :value="values.newDocument"
        @input="handleInput"
      />
      <small v-if="values.newDocument && errors.newDocument" class="text-danger">{{ errors.newDocument }}</small>
      <div class="form-hint">O cliente precisa já estar cadastrado na base.</div>
    </div>

    <div class="alert alert-warning mb-0" role="alert">
      <div class="d-flex align-items-start">
        <IconAlertTriangle class="me-2 flex-shrink-0" />
        <div>
          <strong>Ação irreversível.</strong> O contrato antigo será cancelado e um novo contrato será
          formalizado na cobrança para o novo titular.
        </div>
      </div>
    </div>
  </GenericModal>
</template>
